/**
 * ChatPhotoPreview — confirm-before-send modal for chat photos.
 *
 * After the picker + downscale, the photo is shown full (contained, original
 * aspect) with CANCEL / SEND. Nothing is uploaded until the player confirms.
 * Mirrors the avatar cropper's overlay look; self-contained DOM + styles.
 */

const STYLE_ID = 'chat-photo-preview-styles';

/**
 * Show the photo and resolve true (send) or false (cancel / backdrop tap).
 */
export function confirmChatPhoto(dataUrl: string, container: HTMLElement = document.body): Promise<boolean> {
  injectStyles();

  return new Promise<boolean>((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'chat-photo-preview-overlay';
    overlay.innerHTML = `
      <div class="chat-photo-preview-modal" role="dialog" aria-label="Send photo">
        <div class="chat-photo-preview-title">SEND PHOTO?</div>
        <div class="chat-photo-preview-stage">
          <img src="${dataUrl}" alt="Photo preview">
        </div>
        <div class="chat-photo-preview-actions">
          <button class="chat-photo-preview-btn chat-photo-preview-cancel" type="button">CANCEL</button>
          <button class="chat-photo-preview-btn chat-photo-preview-send" type="button">SEND</button>
        </div>
      </div>
    `;
    container.appendChild(overlay);

    // Commit the hidden base state before the class flip so Android WebView
    // never paints a visible first frame (the flicker rule).
    void overlay.offsetHeight;
    overlay.classList.add('visible');

    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      overlay.classList.remove('visible');
      setTimeout(() => overlay.remove(), 220);
      resolve(ok);
    };

    overlay.querySelector('.chat-photo-preview-cancel')!.addEventListener('click', () => finish(false));
    overlay.querySelector('.chat-photo-preview-send')!.addEventListener('click', () => finish(true));
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) finish(false);
    });
  });
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    .chat-photo-preview-overlay {
      position: fixed;
      inset: 0;
      z-index: 6200;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.7);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    .chat-photo-preview-overlay.visible {
      opacity: 1;
    }

    .chat-photo-preview-modal {
      width: min(92vw, 420px);
      max-height: 86vh;
      display: flex;
      flex-direction: column;
      background: #111114;
      border: 1px solid rgba(255, 0, 128, 0.25);
      border-radius: 16px;
      padding: 16px;
      gap: 12px;
      box-shadow: 0 12px 48px rgba(0, 0, 0, 0.7), 0 0 16px rgba(255, 0, 128, 0.12);
      transform: translateY(12px) scale(0.97);
      transition: transform 0.2s ease;
    }

    .chat-photo-preview-overlay.visible .chat-photo-preview-modal {
      transform: translateY(0) scale(1);
    }

    .chat-photo-preview-title {
      font-family: 'Bebas Neue', sans-serif;
      font-size: 18px;
      letter-spacing: 2px;
      color: #fff;
      text-align: center;
    }

    .chat-photo-preview-stage {
      flex: 1;
      min-height: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #08080A;
      border-radius: 10px;
      overflow: hidden;
    }

    .chat-photo-preview-stage img {
      max-width: 100%;
      max-height: 56vh;
      object-fit: contain;
      display: block;
    }

    .chat-photo-preview-actions {
      display: flex;
      gap: 10px;
    }

    .chat-photo-preview-btn {
      flex: 1;
      padding: 12px 0;
      border: none;
      border-radius: 10px;
      font-family: 'Bebas Neue', sans-serif;
      font-size: 16px;
      letter-spacing: 1.5px;
      cursor: pointer;
      transition: filter 0.15s ease;
    }

    .chat-photo-preview-btn:active {
      filter: brightness(1.2);
    }

    .chat-photo-preview-cancel {
      background: rgba(255, 255, 255, 0.08);
      color: rgba(255, 255, 255, 0.7);
    }

    .chat-photo-preview-send {
      background: #FF0080;
      color: #fff;
      box-shadow: 0 0 12px rgba(255, 0, 128, 0.35);
    }
  `;
  document.head.appendChild(style);
}
